import type { CalendarReminderDTO, TaskDTO } from '@hearth/shared';
import { pluralize } from '../../lib/format';
import { toDateInput } from './entryModel';

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;

/** Dismissed reminders: key → when it was dismissed (ms). */
export type Dismissals = Record<string, number>;

export const DISMISSALS_STORAGE_KEY = 'hearth.reminders.dismissed';
/** Longer than any reminder stays pending on the server (60 min grace), so old keys can go. */
export const DISMISSAL_TTL_MS = 7 * DAY;

/** Identifies one reminder of one schedule: moving the entry or changing its reminder makes it remind again. */
export function reminderKey(entry: Pick<TaskDTO, 'id' | 'dueAt' | 'reminderMinutes'>): string {
  return `${entry.id}:${entry.dueAt}:${entry.reminderMinutes}`;
}

/** Reminders that should be on screen now, earliest first. */
export function dueReminders(items: readonly CalendarReminderDTO[], now: number, dismissed: Dismissals): CalendarReminderDTO[] {
  return items
    .filter((item) => new Date(item.remindAt).getTime() <= now && dismissed[reminderKey(item)] === undefined)
    .sort((a, b) => new Date(a.remindAt).getTime() - new Date(b.remindAt).getTime());
}

/** When the next not-yet-due reminder fires (ms), so a timer can show it on the minute between polls. */
export function nextReminderTime(items: readonly CalendarReminderDTO[], now: number, dismissed: Dismissals): number | null {
  let next: number | null = null;
  for (const item of items) {
    const at = new Date(item.remindAt).getTime();
    if (at > now && dismissed[reminderKey(item)] === undefined && (next === null || at < next)) next = at;
  }
  return next;
}

export function pruneDismissals(dismissed: Dismissals, now: number): Dismissals {
  return Object.fromEntries(Object.entries(dismissed).filter(([, at]) => now - at < DISMISSAL_TTL_MS));
}

/** Parses stored dismissals, dropping anything malformed or expired. */
export function parseDismissals(raw: string | null, now: number): Dismissals {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const valid = Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === 'number');
    return pruneDismissals(Object.fromEntries(valid), now);
  } catch {
    return {};
  }
}

/**
 * When the entry is, relative to now: "in 10 minutes", "now", "started 5 minutes ago" (a time block) /
 * "due 5 minutes ago" (a point in time); all-day entries: "today", "tomorrow", "in 3 days".
 */
export function reminderTimeLabel(entry: Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>, now: number): string {
  if (!entry.dueAt) return '';
  const start = new Date(entry.dueAt);
  if (entry.allDay) {
    const today = new Date(now);
    const days = Math.round(
      (Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / DAY,
    );
    if (days <= 0) return 'today';
    return days === 1 ? 'tomorrow' : `in ${pluralize(days, 'day')}`;
  }
  const minutes = Math.round((start.getTime() - now) / MINUTE);
  if (minutes === 0) return 'now';
  const span = (value: number) => (value < 60 ? pluralize(value, 'minute') : value < 1440 ? pluralize(Math.round(value / 60), 'hour') : pluralize(Math.round(value / 1440), 'day'));
  if (minutes > 0) return `in ${span(minutes)}`;
  return `${entry.endAt ? 'started' : 'due'} ${span(-minutes)} ago`;
}

/** Where "Open" goes: the lead, or the day on the calendar for a personal entry. */
export function reminderHref(entry: Pick<TaskDTO, 'businessId' | 'dueAt'>): string {
  if (entry.businessId) return `/leads/${entry.businessId}`;
  return `/calendar?view=day&date=${entry.dueAt ? toDateInput(new Date(entry.dueAt)) : toDateInput(new Date())}`;
}
